import {json, texture, MiniSignal} from "p3";
import common from "../common";
import definitions from "../definitions";
import CoinAnimation from "../game/actor/misc/CoinAnimation";
import QuestFactory from "../game/quest/QuestFactory";
import ScrollBar from "../ui/ScrollBar";
import TextFormat from "../ui/TextFormat";
import {locale, resizeLabelToFitText} from "../utils";
import GameScene from "./GameScene";
import MenuScene from "./MenuScene";
import Scene from "./Scene";

export default class ShopScene extends MenuScene {
    constructor() {
        super("shop");

        this._menuHolder = null;
        this._menuContent = null;
        this._menuMask = null;
        this._scrollbar = null;
        this._fill = null;

        this._shopItems = [];

        this.signals.locationJump = new MiniSignal();
    }

    init() {
        super.init(); 
    }

    onSceneLoaded() {
        super.onSceneLoaded();

        //console.log(this._questLog);

        this.interactive = true;

        if (!common.saveData.settings.musicMute) {
            P3.audio.playMusic("music_map_loop");
        }
        P3.audio.playSound("vo_bert_hahum_00");

        this.createFill();
 
        this._menuHolder = new PIXI.Container();
        this._menuHolder.x = common.STAGE_WIDTH/2;
        this._menuHolder.y = 150;
        this.addChild(this._menuHolder);

        const backing = new PIXI.NineSlicePlane(texture('shop_bg_9slice'), 60, 40, 60, 40);
        backing.width = 750;
        backing.height = 500;
        backing.position.set(-(backing.width/2), 40);
        this._menuHolder.addChild(backing);

        let menuContentHolder = new PIXI.Container();
        menuContentHolder.y = 30;
        this._menuHolder.addChild(menuContentHolder);

        this._menuContent = new PIXI.Container();
        menuContentHolder.addChild(this._menuContent);

        this._menuMask = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._menuMask.tint = 0x000000;
        this._menuMask.anchor.x = 0.5;
        this._menuMask.y = menuContentHolder.y;
        this._menuMask.width = 750;
        this._menuMask.height = 495;
        menuContentHolder.mask = this._menuMask;
        this._menuHolder.addChild(this._menuMask);

        const shopWindow = new PIXI.Sprite(texture('shop_fg'));
        shopWindow.y = -75;
        shopWindow.anchor.set(0.44, 0);
        this._menuHolder.addChild(shopWindow);

        this.createTitleBanner(locale('ui_shop_title'));

        this._coinHolder = new PIXI.Sprite(texture("panel_coins"));
        this._coinHolder.anchor.set(0.5);
        this._coinHolder.x = -10;
        this._coinHolder.y = 50;
        this._titleBanner.addChild(this._coinHolder);
        
        const coin = new PIXI.Sprite(texture("icon_coin"));
        coin.anchor.set(0.5);
        coin.y = -2;
        this._coinHolder.addChild(coin);
        
        const coinLabel = new PIXI.BitmapText(String(Math.min(999, common.saveData.activeSlot.coins)), {
            fontName: "Alghera Pro",
            fontSize: 32,
            align: "left",
            tint: 0x000000
        });
        coinLabel.anchor.set(0, 0.5);
        coinLabel.x = 17;
        this._coinHolder.label = coinLabel;
        this._coinHolder.addChild(coinLabel);

        this._closeButton = this.addCloseButton(this._menuHolder.x + 400, this._menuHolder.y);
        this._closeButton.overSoundName = "sfx_btn_generic_rollover";
        this._closeButton.clickSoundName = "sfx_btn_quests_close";

        this._fillShelves();

        this.resize();

        this.animateShow();
    }

    destroy() {
        this._closeButton.destroy();

        P3.audio.stopMusic();

        super.destroy();
    }

    update() {
        super.update();

        if(this._scrollbar){
            this._scrollbar.update();
            this._menuContent.y = (-(this._menuContent.height) + this._menuMask.height) * this._scrollbar.value;
        }
    }

    resize()
    {
        super.resize();

        this.resizeFill();
    }

    _fillShelves(){
        const shopData = json('shop');

        const slot = common.saveData.activeSlot;
        const itemsPerShelf = 3;
        let data, button, priceTag, priceHolder, coinIcon, coinLabel;
        const xPositions = [123, 327, 538];
        for (let i = 0; i < Math.ceil(shopData.length / itemsPerShelf); i++) {
            let shelf = new PIXI.Sprite(texture('shop_shelf'));
            shelf.x = -(shelf.width/2);
            shelf.y = i * shelf.height;
            this._menuContent.addChild(shelf);

            for (let j = 0; j < itemsPerShelf; j++) {
                data = shopData[(i*itemsPerShelf)+j];
                if (data) {
                    button = P3.Button.create(
                        texture(data.thumbnail)
                    );
                    button.id = data.id;
                    button.data = data; 
                    button.signal = button.signals.click.add(this._onItemClick, this);
                    button.position.set(xPositions[j], 171 - (button.height/2));
                    this._shopItems.push(button);
                    shelf.addChild(button);

                    priceTag = new PIXI.Sprite(texture('price_tag'));
                    priceTag.position.set(xPositions[j], shelf.height - 30);
                    priceTag.anchor.set(0.5, 0);
                    button.priceTag = priceTag;
                    shelf.addChild(priceTag);

                    priceHolder = new PIXI.Container();
                    
                    coinIcon = new PIXI.Sprite(texture('icon_coin'));
                    coinIcon.scale.set(0.75);
                    priceHolder.addChild(coinIcon);
                    
                    coinLabel = new PIXI.BitmapText(String(data.price), {
                        fontName: "Alghera Pro",
                        fontSize: 25,
                        align: "left",
                        tint: 0x000000
                    });
                    coinLabel.anchor.set(0);
                    coinLabel.position.set(coinIcon.width + 3, 5);
                    priceHolder.addChild(coinLabel);

                    priceHolder.position.set(-(priceHolder.width/2), 4);
                    priceTag.addChild(priceHolder);

                    if (data.quantity > -1 && slot.purchases[data.id] >= data.quantity) {
                        this._sellOut(button);
                    }
                }
            }
        }
    }

    animateShow(){
        this.animateTitleBannerIn();

        this._closeButton.scale.set(0);
        this._closeButton.interactive = false;

        GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {delay:0.2, y:this._menuHolder.y, ease:Expo.easeOut, onComplete:() => {
            
            this.animateCloseButtonIn();
            this.animateShowFinished();

        }}));
        this._menuHolder.y = common.STAGE_HEIGHT + 100;
    }

    animateHide(){
        this.animateFillOut();
        this.animateTitleBannerOut();

        this._closeButton.interactive = false;
        GameScene.active.addTween(gsap.to(this._closeButton.scale, 0.3, {x:0, y:0, ease:Sine.easeIn}));

        if(this._scrollbar){
            this._menuHolder.removeChild(this._scrollbar);
            this._scrollbar.destroy();
        }

        GameScene.active.addTween(gsap.to(this._menuBacking, 0.3, {height:30, ease:Expo.easeOut, onComplete:() => {
            GameScene.active.addTween(gsap.to(this._menuHolder, 0.4, {y:common.STAGE_HEIGHT + 100, ease:Sine.easeIn, onComplete:() => {
                this.signals.previous.dispatch();
            }}));
        }}));
    }

    animateShowFinished()
    {
        if(this._menuContent.height < this._menuMask.height)
            return;

        this._scrollbar = new ScrollBar(texture('scroll_slider'), 245);
        this._scrollbar.y = 50 + (this._scrollbar.height/2) - 10;
        this._scrollbar.x = 340 - (this._scrollbar.width/2) + 14;
        this._menuHolder.addChild(this._scrollbar);
        this._scrollbar.init();
    }

    _onItemClick(button) {
        this._openPurchaseWindow(button);
    }

    _openPurchaseWindow(button) {

        const data = button.data;

        for (let i in this._shopItems) {
            this._shopItems[i].interactive = false;
        }

        this._fill = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._fill.tint = 0x000000;
        this._fill.alpha = 0;
        this.addChild(this._fill);
        this._resizeFill();

        const panel = new PIXI.NineSlicePlane(texture("panel_popup_9slice"), 80, 110, 80, 80);
        panel.width = 470;
        panel.height = 310;
        panel.x = (common.STAGE_WIDTH/2) - (panel.width/2);
        panel.y = common.STAGE_HEIGHT;
        this.addChild(panel);

        const title = new PIXI.BitmapText(locale('ui_shop_buy').toUpperCase(), {
            fontName: "Alghera Pro",
            fontSize: 40,
            align: "center",
            tint: 0x000000
        });
        title.anchor.set(0.5);
        title.position.set(panel.width/2, 60);
        panel.addChild(title);
        resizeLabelToFitText(title, panel.width-60);

        const image = new PIXI.Sprite(texture(data.image));
        image.anchor.set(0.5, 0);
        image.position.set(panel.width/2, 60);
        panel.addChild(image);

        const description = new PIXI.BitmapText(locale(data.name).toUpperCase(), {
            fontName: "Alghera Pro",
            fontSize: 34,
            align: "center",
            tint: 0x000000,
            maxWidth: panel.width-60
        });
        description.anchor.set(0.5);
        description.position.set(panel.width/2, image.y + image.height);
        panel.addChild(description);

        const priceHolder = new PIXI.Container();
                    
        const coinIcon = new PIXI.Sprite(texture('icon_coin'));
        priceHolder.addChild(coinIcon);
        
        const coinLabel = new PIXI.BitmapText(String(data.price), {
            fontName: "Alghera Pro",
            fontSize: 30,
            align: "left",
            tint: 0x000000
        });
        coinLabel.anchor.set(0);
        coinLabel.position.set(coinIcon.width + 3, 5);
        priceHolder.addChild(coinLabel);

        priceHolder.position.set((panel.width/2) - (priceHolder.width/2), description.y + description.height + 10);
        panel.addChild(priceHolder);

        panel.height = Math.max(panel.height, priceHolder.y + priceHolder.height + 30);

        const crossButton = P3.Button.create(
            texture("btn_action_medium_out"),
            null,
            null,
            texture("btn_icon_medium_cross")
        );
        const tickButton = P3.Button.create(
            texture("btn_action_medium_out"),
            null,
            null,
            texture("btn_icon_medium_tick")
        );

        const hidePanel = (callback) => {
            crossButton.signal.detach();
            tickButton.signal.detach();
            this._tweens.push(
                gsap.to(panel, 0.3, {y:common.STAGE_HEIGHT, ease:Expo.easeIn, onComplete:() => {
                    for (let i in this._shopItems) {
                        if (this._shopItems[i].visible) {
                            this._shopItems[i].interactive = true;
                        }
                    }
                    if (callback) {
                        callback();
                    }
                }}),
                gsap.to(this._fill, 0.2, {alpha:0, ease:Sine.easeIn, onComplete:() => {
                    
                }})
            );
        }

        crossButton.signal = crossButton.signals.click.add(() => {
            hidePanel();
            
        }, this);
        crossButton.x = (panel.width/2) - 150;
        crossButton.y = panel.height;
        panel.addChild(crossButton);

        tickButton.signal = tickButton.signals.click.add(() => {
            hidePanel();
            this._purchase(data, button);
        }, this);
        tickButton.x = (panel.width/2) + 150;
        tickButton.y = panel.height;
        panel.addChild(tickButton);

        if (data.price > common.saveData.activeSlot.coins) {
            const colorMatrix = new PIXI.filters.ColorMatrixFilter();
            colorMatrix.desaturate();
            tickButton.filters = [colorMatrix];
            tickButton.interactive = false;
        }

        this._tweens.push(
            gsap.to(panel, 0.3, {y:(common.STAGE_HEIGHT / 2) - (panel.height/2), ease:Expo.easeOut}),
            gsap.to(this._fill, 0.2, {alpha:0.3, ease:Sine.easeIn})
        );
    }

    _purchase(data, button){

        const slot = common.saveData.activeSlot;
        const oldCoins = slot.coins;

        if (slot.purchases == null) {
            slot.purchases = {};
        }
        if (slot.purchases[data.id] == null) {
            slot.purchases[data.id] = 0;
        }
        slot.purchases[data.id]++;
        slot.coins -= data.price;
        common.saveData.saveGame();

        if (data.quantity > -1 && slot.purchases[data.id] >= data.quantity) {
            this._sellOut(button);
        }
        const coinEffect = new CoinAnimation(data.price, GameScene.active, (percent) => {
            this._coinHolder.label.text = oldCoins - Math.round(percent * data.price);
        });
        coinEffect.x = button.x;
        coinEffect.y = button.y;
        button.parent.addChild(coinEffect);
    }

    _sellOut(button) {
        button.visible = button.interactive = false;
        button.priceTag.visible = false;

        const soldOut = new PIXI.BitmapText(locale('ui_shop_sold_out').toUpperCase(), {
            fontName: "Alghera Pro",
            fontSize: 35,
            align: "center",
            tint: 0x000000,
            maxWidth: 150
        });
        soldOut.anchor.set(0.5);
        soldOut.position.set(button.x, button.y - 30);
        button.parent.addChild(soldOut);

        //;
    }

    _resizeFill(){
        if (this._fill) {
            this._fill.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
            this._fill.width = P3.View.width;
            this._fill.height = P3.View.height;
        }
    }
}

/**
 * @static
 * @type {ShopScene}
 */
ShopScene.active = null;